import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactFlow, { Background, BackgroundVariant, MarkerType } from 'reactflow';
import 'reactflow/dist/style.css';
import api from '../../api/axios';
import './LeadAgentJourney.css';

const ACTION_LABELS = {
  sendConnectionRequest: 'LinkedIn-connectie',
  sendMessage: 'LinkedIn-bericht',
  sendEmail: 'E-mail',
  viewProfile: 'Profiel bekeken',
  timer: 'Wachten',
  checkConnectionStatus: 'Connectiestatus check',
  agentHandoff: 'Overdracht aan AI Agent',
  draft_connection_request: 'Connectieverzoek opstellen',
  draft_linkedin_message: 'LinkedIn-bericht opstellen',
  draft_email: 'E-mail opstellen',
  wait: 'Wachten',
  manual_review: 'Handmatige review',
  stop_outreach: 'Outreach stoppen',
};

const STATUS_LABELS = {
  pending: 'Gepland',
  waiting: 'Wacht',
  approval_hold: 'Veilig vastgehouden',
  approving: 'Wordt goedgekeurd',
  approved: 'Goedgekeurd',
  processing: 'Wordt uitgevoerd',
  completed: 'Uitgevoerd',
  failed: 'Mislukt',
  canceled: 'Geannuleerd',
  rejected: 'Afgewezen',
  needs_attention: 'Actie nodig',
};

const EVENT_LABELS = {
  inbound_reply_received: 'Reply ontvangen',
  lead_reply_received: 'Reply ontvangen',
  meeting_booked: 'Afspraak gepland',
  meeting_scheduled: 'Afspraak gepland',
  meeting_cancelled: 'Afspraak geannuleerd',
  meeting_canceled: 'Afspraak geannuleerd',
  meeting_completed: 'Afspraak afgerond',
  meeting_no_show: 'No-show',
  proposal_approval_recovered: 'Approval hersteld',
  workflow_agent_handoff_started: 'Overgedragen vanuit handmatige flow',
  workflow_agent_handoff_completed: 'AI Agent heeft overgenomen',
  outreach_stopped: 'Outreach gestopt',
  lead_outcome_completed: 'Doel bereikt',
};

const HANDOFF_EVENTS = new Set(['workflow_agent_handoff_started', 'workflow_agent_handoff_completed']);

function safeDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDate(value) {
  const date = safeDate(value);
  return date ? date.toLocaleString('nl-NL', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
}

function actionLabel(value) {
  return ACTION_LABELS[value] || String(value || 'Agentactie').replaceAll('_', ' ');
}

function statusLabel(value) {
  return STATUS_LABELS[value] || String(value || '').replaceAll('_', ' ');
}

function eventLabel(value) {
  return EVENT_LABELS[value] || String(value || 'Agent-event').replaceAll('_', ' ');
}

function itemKey(item, index) {
  return String(item?.data?._id || item?.data?.id || `${item?.kind || 'item'}-${item?.at || index}-${index}`);
}

function journeyStep(item, index) {
  const data = item?.data || {};
  const base = { id: itemKey(item, index), at: item?.at || data.createdAt || data.updatedAt || null };

  if (item.kind === 'task') {
    return {
      ...base,
      kind: data.type === 'agentHandoff' ? 'handoff' : 'execution',
      title: actionLabel(data.type),
      status: data.status || 'pending',
      detail: data.type === 'agentHandoff'
        ? 'Vanaf dit punt neemt de doelgestuurde AI Agent deze lead over.'
        : data.errorMessage || (data.status === 'completed' ? 'Actie succesvol uitgevoerd' : 'Uitvoeringstaak'),
    };
  }
  if (item.kind === 'proposal') {
    return {
      ...base,
      kind: 'proposal',
      title: actionLabel(data.actionType),
      status: data.approvalStatus || 'pending',
      detail: data.reason || 'AI-voorstel',
    };
  }
  if (item.kind === 'event') {
    const type = data.type || data.eventType;
    return {
      ...base,
      kind: HANDOFF_EVENTS.has(type) ? 'handoff' : 'event',
      title: eventLabel(type),
      status: type === 'lead_outcome_completed' || type === 'meeting_booked' || type === 'meeting_scheduled' || type === 'workflow_agent_handoff_completed' ? 'completed' : 'event',
      detail: HANDOFF_EVENTS.has(type)
        ? (type === 'workflow_agent_handoff_started' ? 'Einde van de vaste workflow · start van dynamische agentplanning.' : 'De AI Agent plant vanaf hier per lead de beste volgende stap.')
        : data.message || data.reason || data.classification?.intent || 'Agent-event',
    };
  }
  if (item.kind === 'linkedin_observation' && data.observationType === 'connection_state') {
    return {
      ...base,
      kind: 'signal',
      title: 'LinkedIn-status',
      status: data.state === 'connected' ? 'completed' : 'observation',
      detail: data.state === 'connected' ? 'Connectie bevestigd' : `Status: ${data.state || 'onbekend'}`,
    };
  }
  return null;
}

function dedupeSteps(steps) {
  const seen = new Set();
  return steps.filter((step) => {
    if (!step) return false;
    const date = safeDate(step.at);
    const minute = date ? date.toISOString().slice(0, 16) : '';
    const key = `${step.kind}:${step.title}:${step.status}:${minute}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function JourneyNode({ data }) {
  return <div className={`agent-journey-node ${data.variant || ''} status-${data.status || 'unknown'}`}>
    <div className="agent-journey-node-top">
      <span className="agent-journey-kind">{data.kindLabel}</span>
      {data.at && <time>{formatDate(data.at)}</time>}
    </div>
    <strong>{data.title}</strong>
    {data.detail && <p>{data.detail}</p>}
    {data.statusLabel && <span className="agent-journey-status">{data.statusLabel}</span>}
  </div>;
}

const nodeTypes = { journey: JourneyNode };

export default function LeadAgentJourney({ campaignId, leadId }) {
  const [timeline, setTimeline] = useState([]);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const timelineResponse = await api.get(`/agent/campaigns/${campaignId}/leads/${leadId}/timeline`);
      setTimeline(timelineResponse.data.timeline || []);
      try {
        const planResponse = await api.get(`/agent/campaigns/${campaignId}/leads/${leadId}/plan/latest`);
        setPlan(planResponse.data?.plan || null);
      } catch {
        setPlan(null);
      }
      setError('');
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.response?.data?.error || requestError.message);
    } finally {
      setLoading(false);
    }
  }, [campaignId, leadId]);

  useEffect(() => { load(); }, [load]);

  const steps = useMemo(() => {
    const chronological = [...timeline].sort((a, b) => (safeDate(a.at)?.getTime() || 0) - (safeDate(b.at)?.getTime() || 0));
    const meaningful = dedupeSteps(chronological.map(journeyStep)).slice(-12);
    const result = [{ id: 'journey-start', kind: 'start', title: 'Lead gestart', status: 'completed', detail: 'Leadgenflow volgt deze lead binnen de campagne.' }, ...meaningful];
    if (plan?.action) {
      result.push({
        id: 'journey-next',
        kind: 'next',
        title: actionLabel(plan.action),
        status: 'planned',
        at: plan.nextPlanningAt || plan.followUpAt || null,
        detail: plan.reason || `Volgende stap via ${plan.channel || 'agent'}`,
      });
    }
    return result;
  }, [timeline, plan]);

  const nodes = useMemo(() => steps.map((step, index) => ({
    id: step.id,
    type: 'journey',
    position: { x: index * 260, y: index % 2 === 0 ? 20 : 125 },
    draggable: false,
    selectable: true,
    data: {
      ...step,
      variant: step.kind === 'next' ? 'next' : step.kind === 'start' ? 'start' : step.kind === 'handoff' ? 'handoff' : '',
      kindLabel: step.kind === 'proposal' ? 'AI voorstel' : step.kind === 'execution' ? 'Uitvoering' : step.kind === 'event' ? 'Gebeurtenis' : step.kind === 'handoff' ? 'Overdracht' : step.kind === 'signal' ? 'Signaal' : step.kind === 'next' ? 'Volgende stap' : 'Start',
      statusLabel: step.kind === 'next' ? 'Door planner voorzien' : step.kind === 'handoff' ? 'Manual → AI Agent' : statusLabel(step.status),
    },
  })), [steps]);

  const edges = useMemo(() => nodes.slice(1).map((node, index) => ({
    id: `journey-edge-${index}`,
    source: nodes[index].id,
    target: node.id,
    type: 'smoothstep',
    markerEnd: { type: MarkerType.ArrowClosed },
    style: { strokeWidth: node.data?.variant === 'handoff' ? 3 : 2 },
  })), [nodes]);

  return <section className="lead-agent-card agent-journey-card">
    <div className="lead-agent-section-header">
      <div>
        <h2>Agent Journey</h2>
        <p>Visuele route van vaste workflowstappen, agentvoorstellen en uitgevoerde acties — inclusief het moment waarop de AI Agent het overneemt.</p>
      </div>
      <button className="lead-agent-button secondary" onClick={load} disabled={loading}>{loading ? 'Laden…' : 'Vernieuwen'}</button>
    </div>
    {error ? <p className="agent-journey-error">{error}</p> : <div className="agent-journey-flow">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.18, maxZoom: 0.9 }}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable
        zoomOnDoubleClick={false}
        minZoom={0.35}
        maxZoom={1.1}
        proOptions={{ hideAttribution: true }}
      >
        <Background variant={BackgroundVariant.Dots} gap={18} size={1} />
      </ReactFlow>
    </div>}
  </section>;
}
